/**
 * Public-facing renderer for tour page media_blocks.
 * Renders each block in its visual style. Used inside TourDetail.
 */
import MediaEmbed from "./MediaEmbed";
import { normalizeImageUrl } from "../lib/imageUrl";

export default function MediaBlocksRenderer({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <div className="space-y-14" data-testid="media-blocks-renderer">
      {blocks.map((b, i) => <Block key={b.id || i} block={b} />)}
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "heading":
      return (
        <h2 className="serif text-ms-ink text-3xl md:text-4xl" data-testid="mb-r-heading">
          {block.text}
        </h2>
      );
    case "paragraph":
      return (
        <p className="text-ms-ink text-base md:text-lg leading-[1.75] whitespace-pre-line max-w-2xl" data-testid="mb-r-paragraph">
          {block.text}
        </p>
      );
    case "image":
      return (
        <figure className="space-y-3" data-testid="mb-r-image">
          {block.src && (
            <div className="overflow-hidden rounded-sm bg-ms-clay border border-ms-edge">
              <img src={normalizeImageUrl(block.src)} alt={block.caption || ""} className="w-full max-h-[640px] object-cover" />
            </div>
          )}
          {block.caption && (
            <figcaption className="text-xs text-ms-stone italic text-center">{block.caption}</figcaption>
          )}
        </figure>
      );
    case "video":
      if (!block.url) return null;
      return (
        <div className="aspect-video w-full overflow-hidden rounded-sm border border-ms-edge bg-ms-ink-deep" data-testid="mb-r-video">
          <MediaEmbed url={block.url} mode="trailer" />
        </div>
      );
    case "gallery": {
      const imgs = block.images || [];
      if (!imgs.length) return null;
      const cols = imgs.length >= 4 ? "md:grid-cols-4" : imgs.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
      return (
        <div className={`grid grid-cols-2 ${cols} gap-3`} data-testid="mb-r-gallery">
          {imgs.map((g, i) => (
            <div key={i} className="aspect-square overflow-hidden bg-ms-clay rounded-sm border border-ms-edge">
              <img src={normalizeImageUrl(g)} alt="" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700" />
            </div>
          ))}
        </div>
      );
    }
    case "quote":
      return (
        <blockquote className="border-l-4 border-ms-brass pl-6 py-2 max-w-2xl" data-testid="mb-r-quote">
          <p className="serif italic text-2xl md:text-3xl text-ms-ink leading-snug">"{block.text}"</p>
          {block.attribution && (
            <footer className="mt-3 label-eyebrow text-ms-brass text-xs">{block.attribution}</footer>
          )}
        </blockquote>
      );
    case "two_column": {
      const isImageRight = block.layout === "image-right";
      const ImageEl = block.image && (
        <div className="overflow-hidden rounded-sm bg-ms-clay border border-ms-edge">
          <img src={normalizeImageUrl(block.image)} alt="" className="w-full h-full object-cover aspect-[4/3]" />
        </div>
      );
      const TextEl = block.text && (
        <p className="text-ms-ink text-base md:text-lg leading-[1.75] whitespace-pre-line">{block.text}</p>
      );
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center" data-testid="mb-r-two-column">
          {isImageRight ? <>{TextEl}{ImageEl}</> : <>{ImageEl}{TextEl}</>}
        </div>
      );
    }
    default:
      return null;
  }
}
